/*
Purpose: View and manage a single casefile and its contents.
Audience: Case owners and collaborators.
What it does:
- Shows case metadata, evidence list, and tools for editing/adding items
Interplay:
- Links to EvidenceModal for item-level actions; uses UniversalSidebar for navigation
*/
import React, {useState, useEffect, useCallback, useRef} from 'react';
import {useParams, useNavigate} from 'react-router-dom';
import {doc, getDoc, updateDoc, deleteDoc, collection, query, getDocs} from 'firebase/firestore';
import {ref, deleteObject} from 'firebase/storage';
import {db, storage} from '../../firebase';
import EvidenceUploader from '../evidence/EvidenceUploader';
import EvidenceList from '../evidence/EvidenceList';
import CasefileTrackerTab from './CasefileTrackerTab';
import CasefileSpotsTab from './CasefileSpotsTab';
import CasefileForm from './CasefileForm';
import SlideToDelete from '../buttons/SlideToDelete';
import CasefileToolbar from './CasefileToolbar';
import CasefileNavigationSidebar from './CasefileNavigationSidebar';
import CasefilePrivacySettings from './CasefilePrivacySettings';
import { FaInfoCircle } from 'react-icons/fa';
import '../../styles/CasefileDetail.css';
import { useNotifications } from '../notifications/NotificationsProvider';

// Utility function to track recently viewed casefiles
const addToRecentlyViewed = (casefile) => {
    try {
        const recentlyViewed = JSON.parse(localStorage.getItem('recentlyViewedCases') || '[]');
        const filtered = recentlyViewed.filter(item => item.id !== casefile.id);
        const updated = [{
            id: casefile.id,
            title: casefile.title,
            isPublic: casefile.isPublic,
            viewedAt: new Date().toISOString(),
            createdAt: casefile.createdAt ? casefile.createdAt.toDate().toISOString() : null
        }, ...filtered];
        const trimmed = updated.slice(0, 5);
        localStorage.setItem('recentlyViewedCases', JSON.stringify(trimmed));
    } catch (error) {
        console.error('Error saving to recently viewed:', error);
    }
};


// --- Main CasefileDetail Component ---
const CasefileDetail = ({user, authIsLoading, sidebarOpen = false, onSidebarToggle}) => {
    const {casefileId} = useParams();
    const navigate = useNavigate();
    const [casefile, setCasefile] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const [isConfirmModalOpen, setConfirmModalOpen] = useState(false);
    const [isEditModalOpen, setEditModalOpen] = useState(false);
    const [isUploadModalOpen, setUploadModalOpen] = useState(false);
    const [selectedEvidence, setSelectedEvidence] = useState([]); // State for selected evidence
    const [showInfoPopup, setShowInfoPopup] = useState(false);
    const [activeTab, setActiveTab] = useState('evidence'); // 'evidence' | 'tracker' | 'spots'
    const [evidenceSubTab, setEvidenceSubTab] = useState('uploaded'); // 'uploaded' | 'bookmarks'
    const [marksView, setMarksView] = useState('evidence'); // 'evidence' | 'jobs' (used for Bookmarks toggle)
    const infoPopupRef = useRef(null);
    const userId = user?.uid;

    const { notify } = useNotifications();

    const fetchCasefile = useCallback(async () => {
        setIsLoading(true);
        setError('');
        try {
            const docRef = doc(db, 'casefiles', casefileId);
            const docSnap = await getDoc(docRef);
            if (docSnap.exists()) {
                const casefileData = docSnap.data();
                const isOwner = userId && casefileData.createdBy === userId;
                if (casefileData.isPublic || isOwner) {
                    const casefileWithId = {id: docSnap.id, ...casefileData};
                    setCasefile(casefileWithId);
                    addToRecentlyViewed(casefileWithId);
                } else {
                    setError('Access Denied. This is a private casefile.');
                }
            } else {
                setError('Casefile not found.');
            }
        } catch (err) {
            console.error("Error fetching casefile document:", err);
            setError('Failed to load casefile data.');
        }
        setIsLoading(false);
    }, [casefileId, userId]);
    
    useEffect(() => {
        if (authIsLoading) return;
        if (casefileId) {
            fetchCasefile();
        }
    }, [casefileId, userId, authIsLoading, fetchCasefile]);

    // Close info popup when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (infoPopupRef.current && !infoPopupRef.current.contains(event.target)) {
                setShowInfoPopup(false);
            }
        };

        if (showInfoPopup) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showInfoPopup]);

    // Handles toggling the selection of an evidence item
    const handleEvidenceSelectionChange = (evidenceItem, isSelected) => {
        setSelectedEvidence(prevSelected => {
            if (isSelected) {
                // Add item if not already present
                return [...prevSelected, evidenceItem];
            } else {
                // Remove item
                return prevSelected.filter(item => item.id !== evidenceItem.id);
            }
        });
    };

    const handlePublicToggle = async () => {
        if (!casefile) return;
        const newPublicStatus = !casefile.isPublic; // Toggle the current status
        setCasefile(prevCasefile => ({...prevCasefile, isPublic: newPublicStatus}));
        const docRef = doc(db, 'casefiles', casefileId);
        try {
            await updateDoc(docRef, {isPublic: newPublicStatus});
            notify(`Casefile is now ${newPublicStatus ? 'public' : 'private'}`, 'success');
        } catch (error) {
            console.error("Error updating public status:", error);
            setCasefile(prevCasefile => ({...prevCasefile, isPublic: !newPublicStatus}));
            notify('Failed to update casefile visibility', 'error');
        }
    };

    const handleUpdateSuccess = (updatedData) => {
        setCasefile(prevCasefile => ({...prevCasefile, ...updatedData}));
        setEditModalOpen(false);
    };
    
    const handleUploadComplete = () => {
        setUploadModalOpen(false);
    };

    const handleDeleteCasefile = async () => {
        if (!casefile || casefile.createdBy !== userId) {
            notify({ severity: 'error', title: 'Permission Denied', message: 'You do not have permission to delete this casefile.' });
            return;
        }
        try {
            const evidenceQuery = query(collection(db, 'casefiles', casefileId, 'evidence'));
            const evidenceDocs = await getDocs(evidenceQuery);
            const deletionPromises = evidenceDocs.docs.map(evidenceDoc => {
                const evidenceData = evidenceDoc.data();
                const promises = [];
                if (evidenceData.filePath) {
                    promises.push(deleteObject(ref(storage, evidenceData.filePath)));
                }
                promises.push(deleteDoc(doc(db, 'casefiles', casefileId, 'evidence', evidenceDoc.id)));
                return Promise.all(promises);
            });
            
            await Promise.all(deletionPromises);
            await deleteDoc(doc(db, 'casefiles', casefileId));
            navigate('/dashboard');
        } catch (err) {
            console.error("Error deleting casefile and associated data:", err);
            notify({ severity: 'error', title: 'Deletion Failed', message: 'Please check the console for details.' });
        } finally {
            setConfirmModalOpen(false);
        }
    };

    if (authIsLoading) return <div className="casefile-detail-page"><p>Authenticating...</p></div>;
    if (isLoading) return <div className="casefile-detail-page"><p>Loading casefile details...</p></div>;
    if (error) return <div className="casefile-detail-page"><p style={{color: '#ff4d4d'}}>{error}</p></div>;
    if (!casefile) return null;

    const isOwner = userId && userId === casefile.createdBy;

    // Mock function for opening case results - you'll need to implement this
    const openCaseResults = () => {
        console.log('Opening case results...');
        // Add your case results opening logic here
    };

    // Toggle navigation sidebar
    const toggleNavigationSidebar = (isOpen) => {
        if (onSidebarToggle) {
            onSidebarToggle(isOpen);
        }
    };

    return (
        <div className="casefile-detail-container file-explorer-layout">
            {/* Navigation Sidebar */}
            <CasefileNavigationSidebar
                user={user}
                isOpen={sidebarOpen}
                onToggle={toggleNavigationSidebar}
            />
            
            {/* Main Content Area */}
            <div className={`casefile-main-content ${sidebarOpen ? 'sidebar-open' : ''}`}>
                {/* Top Toolbar */}
                <CasefileToolbar
                    isOwner={isOwner}
                    isPublic={!!casefile.isPublic}
                    onBack={() => navigate(-1)}
                    onUpload={() => setUploadModalOpen(true)}
                    onEdit={() => setEditModalOpen(true)}
                    onPublicToggle={handlePublicToggle}
                    onDelete={() => setConfirmModalOpen(true)}
                    onResults={openCaseResults}
                    showNavigationToggle={false}
                />
                
                <div className="casefile-detail-content">
                    {/* Modern Case Header */}
                    <div className="modern-case-header">
                        <div className="case-header-main">
                            <div className="title-with-badge">
                                <h1 className="case-title">{casefile.title}</h1>
                                {casefile.isPublic ? (
                                    <span className="privacy-badge public" title="This casefile is visible to everyone">
                                        🌐 Public
                                    </span>
                                ) : (
                                    <span className="privacy-badge private" title="This casefile is only visible to you">
                                        🔒 Private
                                    </span>
                                )}
                            </div>
                            <button 
                                className={`case-details-toggle ${showInfoPopup ? 'active' : ''}`}
                                onClick={() => setShowInfoPopup(!showInfoPopup)}
                                title={showInfoPopup ? "Hide Details" : "Show Details"}
                            >
                                <FaInfoCircle />
                                <span className="toggle-text">Details</span>
                            </button>
                        </div>

                        {/* Expandable Details Panel */}
                        {showInfoPopup && (
                            <div className="case-details-panel" ref={infoPopupRef}>
                                <div className="details-grid">
                                    <div className="detail-section">
                                        <h3>Case Information</h3>
                                        <div className="detail-item">
                                            <span className="detail-label">Case ID:</span>
                                            <span className="detail-value">{casefile.id}</span>
                                        </div>
                                        <div className="detail-item">
                                            <span className="detail-label">Created:</span>
                                            <span className="detail-value">
                                                {casefile.createdAt ? new Date(casefile.createdAt.toDate()).toLocaleDateString('en-US', { 
                                                    year: 'numeric', 
                                                    month: 'long', 
                                                    day: 'numeric',
                                                    hour: '2-digit',
                                                    minute: '2-digit'
                                                }) : 'N/A'}
                                            </span>
                                        </div>
                                        <div className="detail-item">
                                            <span className="detail-label">Status:</span>
                                            <span className={`detail-value status-badge ${casefile.isPublic ? 'public' : 'private'}`}>
                                                {casefile.isPublic ? 'Public' : 'Private'}
                                            </span>
                                        </div>
                                    </div>
                                    
                                    {(casefile.description || (casefile.categories && casefile.categories.length > 0)) && (
                                        <div className="detail-section">
                                            <h3>Case Details</h3>
                                            {casefile.description && (
                                                <div className="detail-item description">
                                                    <span className="detail-label">Description:</span>
                                                    <p className="detail-description">{casefile.description}</p>
                                                </div>
                                            )}
                                            {casefile.categories && casefile.categories.length > 0 && (
                                                <div className="detail-item">
                                                    <span className="detail-label">Categories:</span>
                                                    <div className="category-tags">
                                                        {casefile.categories.map((category) => (
                                                            <span key={category} className="category-tag">{category}</span>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                                
                                {/* Privacy Settings Panel - Only for Owner */}
                                {isOwner && (
                                    <CasefilePrivacySettings 
                                        casefile={casefile}
                                        isOwner={isOwner}
                                        onUpdate={fetchCasefile}
                                    />
                                )}
                            </div>
                        )}
                    </div>

                {/* Tabs */}
                <div className="casefile-tabs">
                    <div className="tabs-header">
                        <button 
                            className={`tab-btn ${activeTab === 'evidence' ? 'active' : ''}`}
                            onClick={() => setActiveTab('evidence')}
                            title="Evidence uploaded to this casefile"
                        >Evidence <span className="help-tip">?</span></button>
                        <button 
                            className={`tab-btn ${activeTab === 'tracker' ? 'active' : ''}`}
                            onClick={() => setActiveTab('tracker')}
                            title="Your orders for this case and your external jobs"
                        >Tracker <span className="help-tip">?</span></button>
                        <button 
                            className={`tab-btn ${activeTab === 'spots' ? 'active' : ''}`}
                            onClick={() => setActiveTab('spots')}
                            title="Similar jobs outside this case, from other users"
                        >Spots <span className="help-tip">?</span></button>
                    </div>

                    {/* Tab Content */}
                    {activeTab === 'evidence' && (
                        <div className="evidence-section">
                            {/* Evidence sub-tabs */}
                            <div className="tabs-header sub-tabs">
                                <button 
                                    className={`tab-btn ${evidenceSubTab === 'uploaded' ? 'active' : ''}`}
                                    onClick={() => setEvidenceSubTab('uploaded')}
                                    title="Evidence uploaded to this case"
                                >Uploaded <span className="help-tip">?</span></button>
                                <button 
                                    className={`tab-btn ${evidenceSubTab === 'bookmarks' ? 'active' : ''}`}
                                    onClick={() => setEvidenceSubTab('bookmarks')}
                                    title="Bookmarks: pinned evidence and jobs"
                                >Bookmarks <span className="help-tip">?</span></button>
                            </div>

                            {/* Uploaded */}
                            {evidenceSubTab === 'uploaded' && (
                                (casefile.isPublic || isOwner) && (
                                    <EvidenceList
                                        casefileId={casefileId}
                                        userId={userId}
                                        currentUser={user}
                                        selectedEvidence={selectedEvidence}
                                        onSelectionChange={handleEvidenceSelectionChange}
                                        casefileName={casefile?.name || casefile?.title || 'Untitled Casefile'}
                                    />
                                )
                            )}

                            {/* Bookmarks with toggle for Evidence vs Jobs */}
                            {evidenceSubTab === 'bookmarks' && (
                                <div className="content-box">
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                            <strong>Bookmarks</strong>
                                            <span className="help-tip" title="Toggle between bookmarked evidence and bookmarked jobs">?</span>
                                        </div>
                                        <div style={{ display: 'flex', gap: 8 }}>
                                            <button className={`btn-secondary btn-sm ${marksView==='evidence' ? 'active' : ''}`} onClick={()=>setMarksView('evidence')}>Evidence</button>
                                            <button className={`btn-secondary btn-sm ${marksView==='jobs' ? 'active' : ''}`} onClick={()=>setMarksView('jobs')}>Jobs</button>
                                        </div>
                                    </div>
                                    <div style={{ opacity: 0.8 }}>
                                        {marksView === 'evidence' ? 'Placeholder: your bookmarked evidence will show here.' : 'Placeholder: your bookmarked jobs will show here.'}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {activeTab === 'tracker' && (
                        <div className="tracker-section">
                            <CasefileTrackerTab 
                                casefileId={casefileId}
                                currentUser={user}
                            />
                        </div>
                    )}

                    {activeTab === 'spots' && (
                        <div className="spots-section">
                            <CasefileSpotsTab 
                                casefileId={casefileId}
                                currentUser={user}
                            />
                        </div>
                    )}
                </div>
                </div>
            </div>

            {/* --- Modals --- */}
            {isConfirmModalOpen && (
                <div className="modal-backdrop">
                    <div className="modal-content confirmation-modal">
                        <h3>Confirm Deletion of Casefile</h3>
                        <p>This action is permanent and cannot be undone.</p>
                        <SlideToDelete
                            onConfirm={handleDeleteCasefile}
                            onCancel={() => setConfirmModalOpen(false)}
                        />
                    </div>
                </div>
            )}

            {isEditModalOpen && (
                <div className="modal-backdrop">
                    <div className="modal-content">
                        <CasefileForm
                            user={user}
                            existingCasefile={casefile}
                            onClose={() => setEditModalOpen(false)}
                            onUpdateSuccess={handleUpdateSuccess}
                        />
                    </div>
                </div>
            )}
            
            {isUploadModalOpen && (
                <div className="modal-backdrop">
                    <div className="modal-content">
                        <EvidenceUploader
                            casefileId={casefileId}
                            onUploadComplete={handleUploadComplete}
                            onClose={() => setUploadModalOpen(false)}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

export default CasefileDetail;